import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
    authApi,
    LoginRequest,
    RegisterRequest,
    AuthResponse,
} from "@/api/auth";
import { queryClient } from "@/main";
import { ViewMode, canAccessAdmin } from "@/utils/roleUtils";

interface User {
    id: number;
    email: string;
    fullName: string;
    role: string; // 'admin' | 'member'
    passwordChangedAt?: string | null;
    phoneNumber?: string | null;
    countryCode?: string | null;
    isPhoneVerified?: boolean;
}

interface AuthState {
    user: User | null;
    accessToken: string | null;
    refreshToken: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    hasCheckedAuth: boolean; // 인증 확인이 완료되었는지 여부
    isHydrated: boolean; // Zustand persist rehydration 완료 여부
    error: string | null;
    currentMode: ViewMode; // 현재 뷰 모드 (admin | learner)
    requiresSetup: boolean; // 초기 설정 필요 여부

    // Actions
    login: (data: LoginRequest) => Promise<void>;
    register: (data: RegisterRequest) => Promise<void>;
    logout: () => void;
    checkAuth: () => Promise<void>;
    clearError: () => void;
    setHydrated: () => void; // rehydration 완료 표시
    setCurrentMode: (mode: ViewMode) => void; // 뷰 모드 전환
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            hasCheckedAuth: false, // 초기값은 false
            isHydrated: false, // rehydration 완료 전에는 false
            error: null,
            currentMode: 'admin', // 기본 모드는 admin
            requiresSetup: false,

            setHydrated: () => {
                set({ isHydrated: true });
            },

            setCurrentMode: (mode) => {
                set({ currentMode: mode });
            },

            login: async (data) => {
                set({ isLoading: true, error: null });

                try {
                    const response = await authApi.login(data);
                    const authData = response.data.data as AuthResponse;
                    const { user, accessToken, refreshToken } = authData;
                    const requiresSetup = authData.requiresSetup || false;
                    console.log("Logged in user:", user);

                    // userType이 employee이고 role이 없으면 admin API 호출해서 확인
                    // 일단 role이 없으면 admin으로 가정 (B2B 관리자 로그인)
                    const userRole = user.role || "admin";
                    console.log("Login user role:", userRole, "original:", user.role);

                    // 로그인 시 currentMode 설정: admin 권한이 있으면 admin, 없으면 learner
                    const initialMode: ViewMode = canAccessAdmin(userRole) ? 'admin' : 'learner';

                    set({
                        user: {
                            id: user.id,
                            email: user.email,
                            fullName: user.fullName,
                            role: userRole,
                            passwordChangedAt: user.passwordChangedAt,
                            phoneNumber: user.phoneNumber,
                            countryCode: user.countryCode,
                            isPhoneVerified: user.isPhoneVerified,
                        },
                        accessToken,
                        refreshToken,
                        isAuthenticated: true,
                        isLoading: false,
                        hasCheckedAuth: true, // 로그인 시 인증 확인 완료
                        isHydrated: true, // 로그인 시 hydrated로 표시
                        currentMode: initialMode,
                        requiresSetup,
                    });
                } catch (error: any) {
                    set({
                        isLoading: false,
                        error:
                            error.response?.data?.error?.code || error.response?.data?.message ||
                            "LOGIN_FAILED",
                    });
                    throw error;
                }
            },

            register: async (data) => {
                set({ isLoading: true, error: null });

                try {
                    await authApi.register(data);
                    set({ isLoading: false });
                } catch (error: any) {
                    set({
                        isLoading: false,
                        error:
                            error.response?.data?.error?.code || error.response?.data?.message ||
                            "REGISTER_FAILED",
                    });
                    throw error;
                }
            },

            logout: () => {
                // React Query 캐시 초기화 (이전 사용자 데이터 제거)
                queryClient.clear();

                set({
                    user: null,
                    accessToken: null,
                    refreshToken: null,
                    isAuthenticated: false,
                    requiresSetup: false,
                    // hasCheckedAuth는 유지 (로그아웃 후에도 이미 확인했다는 상태 유지)
                });
            },

            checkAuth: async () => {
                // App.tsx에서 isHydrated가 true일 때만 호출하므로
                // 여기서는 바로 인증 확인 진행
                const { accessToken } = get();

                if (!accessToken) {
                    set({
                        isAuthenticated: false,
                        isLoading: false,
                        hasCheckedAuth: true, // 확인 완료
                    });
                    return;
                }

                set({ isLoading: true });

                try {
                    // 타임아웃 설정 (10초)
                    const timeoutPromise = new Promise((_, reject) => {
                        setTimeout(
                            () => reject(new Error("Request timeout")),
                            10000
                        );
                    });

                    const response = (await Promise.race([
                        authApi.me(),
                        timeoutPromise,
                    ])) as any;

                    // ApiResponse<UserResponse> 형태이므로 response.data.data를 사용
                    const user = response.data.data;

                    console.log("checkAuth user:", user);

                    const userRole = user.role || "admin";
                    console.log("checkAuth user role:", userRole, "original:", user.role);

                    // checkAuth 시 currentMode는 건드리지 않음 (localStorage에서 복원된 사용자 선택 유지)
                    set({
                        user: {
                            id: user.id,
                            email: user.email,
                            fullName: user.fullName,
                            role: userRole,
                            passwordChangedAt: user.passwordChangedAt,
                            phoneNumber: user.phoneNumber,
                            countryCode: user.countryCode,
                            isPhoneVerified: user.isPhoneVerified,
                        },
                        isAuthenticated: true,
                        isLoading: false,
                        hasCheckedAuth: true,
                    });
                } catch (error: any) {
                    console.error("checkAuth error:", error);
                    console.error("Error details:", error.response?.data);

                    // 토큰 만료 시 갱신 시도
                    if (error.response?.status === 401) {
                        const { refreshToken } = get();
                        if (refreshToken) {
                            try {
                                const refreshResponse = await authApi.refresh(
                                    refreshToken
                                );
                                const {
                                    user,
                                    accessToken: newAccessToken,
                                    refreshToken: newRefreshToken,
                                } = refreshResponse.data.data;

                                const refreshedRole = user.role || "admin";

                                // 토큰 갱신 시에도 currentMode는 건드리지 않음
                                set({
                                    user: {
                                        id: user.id,
                                        email: user.email,
                                        fullName: user.fullName,
                                        role: refreshedRole,
                                        passwordChangedAt: user.passwordChangedAt,
                                        phoneNumber: user.phoneNumber,
                                        countryCode: user.countryCode,
                                        isPhoneVerified: user.isPhoneVerified,
                                    },
                                    accessToken: newAccessToken,
                                    refreshToken: newRefreshToken,
                                    isAuthenticated: true,
                                    isLoading: false,
                                    hasCheckedAuth: true,
                                });
                                return;
                            } catch (refreshError) {
                                console.error(
                                    "Failed to refresh token:",
                                    refreshError
                                );
                                // 갱신 실패 시 로그아웃 진행
                            }
                        }
                    }

                    // 토큰 만료 등의 이유로 실패하면 로그아웃
                    // 상태 업데이트 및 localStorage 정리
                    set({
                        user: null,
                        accessToken: null,
                        refreshToken: null,
                        isAuthenticated: false,
                        isLoading: false,
                        hasCheckedAuth: true, // 확인 완료 - 에러가 발생해도 로딩 화면에서 벗어나게 함
                    });
                    // localStorage에서도 인증 정보 제거 (일관성 유지)
                    try {
                        localStorage.removeItem("auth-storage");
                    } catch (e) {
                        // localStorage 접근 실패는 무시
                    }
                }
            },

            clearError: () => set({ error: null }),
        }),
        {
            name: "auth-storage",
            partialize: (state) => ({
                // hasCheckedAuth와 isHydrated는 저장하지 않음 (항상 false로 시작)
                user: state.user,
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
                currentMode: state.currentMode, // currentMode는 저장 (사용자 선택 유지)
                requiresSetup: state.requiresSetup,
                // isAuthenticated도 저장하지 않음 (항상 checkAuth()로 확인)
            }),
            onRehydrateStorage: () => {
                // rehydration 시작 시점
                return (state, error) => {
                    if (error) {
                        console.error("Failed to rehydrate auth state:", error);
                        // 에러 발생 시 모든 상태 초기화
                        if (state) {
                            state.user = null;
                            state.accessToken = null;
                            state.refreshToken = null;
                            state.isAuthenticated = false;
                            state.hasCheckedAuth = false;
                            state.isHydrated = true; // 에러여도 hydrated로 표시하여 무한 대기 방지
                        }
                        return;
                    }

                    // localStorage에서 상태 복원 후, 인증 확인이 완료될 때까지
                    // isAuthenticated를 false로 초기화하여 checkAuth()가 완료될 때까지
                    // 인증 상태가 신뢰되지 않도록 함
                    if (state) {
                        // 복원된 토큰이 있어도 일단 인증 상태는 false로 시작
                        state.isAuthenticated = false;
                        state.hasCheckedAuth = false;
                        state.isHydrated = true; // rehydration 완료 표시
                        // B2B Company 앱에서는 기본 role을 admin으로 설정
                        // 이전에 저장된 role 값이 있어도 admin으로 재설정
                        if (state.user) {
                            state.user.role = "admin";
                        }
                        // URL 경로 기반으로 currentMode 동기화
                        // 새로고침 시 localStorage의 currentMode가 실제 URL과 불일치하는 문제 방지
                        const pathname = window.location.pathname;
                        if (pathname.startsWith('/admin')) {
                            state.currentMode = 'admin';
                        } else if (pathname.startsWith('/my') || pathname.startsWith('/learn') || pathname.startsWith('/messages') || pathname.startsWith('/review-contents')) {
                            state.currentMode = 'learner';
                        }
                    }
                };
            },
        }
    )
);
